<template>
  <main class="c-white bg-black" style="--theme: #00ff73">
    <!-- Hero section -->
    <div class="mx-a" style="max-width: 120rem">
      <base-picture v-bind="hero.background" class="full top-0 fixed lg:w-a" />
      <div class="mb-10 relative">
        <base-picture
          v-style:h="{ sm: 'calc(100vh - 14.93vw)', md: 'auto' }"
          v-bind="hero.image"
          class="w-full relative"
          fit="cover"
        />
        <div class="flex items-center md:absolute-0 bottom-0 absolute">
          <div v-style:p="{ sm: '5rem 2rem', md: '6rem 0 0 5rem', lg: '9rem 0 0 7.5rem' }" class="space-y-4">
            <h2
              v-style:text-size="{ sm: '14vw', md: 'min(5vw, 5.625rem)' }"
              v-html="hero.title"
              class="c-$theme fw-bold"
              style="line-height: .85"
            />
            <p class="fw-medium w-4/5 lg:text-base md:text-xs md:w-3/5">
              {{ hero.description }}
            </p>
            <vf-button to="#how-it-works" variant="themable-inverse">
              {{ $t.learnMore }}
            </vf-button>
          </div>
        </div>
      </div>
      <!-- Main Content -->
      <div class="mx-a mt-10 relative z-1 space-y-15" style="max-width: 75rem">
        <!-- How it works -->
        <section id="how-it-works" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="c-$theme mx-8 py-4 text-center b-y b-white h-15 md:mx-5">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="text-xl fw-bold px-4 py-2 bg-black inline relative lg:text-4xl" style="top: -2rem">
              {{ howItWorks.heading }}
            </h2>
          </div>
          <p class="text-xs ml-5 mt-5 <md:text-center">
            {{ howItWorks.subheading }}
          </p>
          <vf-carousel class="mt-10 w-full <md:pl-8">
            <div
              v-for="({ body, heading, icon }, key) in howItWorks.slides"
              :key
              class="mb-15 pr-4 f-col w-4/5 md:flex-row md:w-3/8"
            >
              <h2 v-style:text-size="{ sm: '15.5vw', md: '7rem', lg: '10rem' }" class="c-$theme fw-bold lh-none ml-2 flex w-1/4 items-end">
                {{ key + 1 }}
              </h2>
              <div class="ml-2 b-t-2 b-white space-y-4 md:w-4/5">
                <base-picture v-bind="icon" class="mt-4" />
                <h2 class="text-lg fw-bold lg:text-lg md:text-xs">
                  {{ heading }}
                </h2>
                <p v-style:text-size="{ sm: '.75rem', md: '.55rem', lg: '.875rem' }" class="lh-none">
                  <base-interpolator :content="body">
                    <template #link="{ args: [message] }">
                      <vf-link class="fw-bold" to="#pricing">
                        {{ message }}
                      </vf-link>
                    </template>
                  </base-interpolator>
                </p>
              </div>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="bottom-0 right-3/4 absolute lg:right-1/5 md:right-1/4"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="bottom-0 right-1/4 absolute md:right-0"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="rounded-full bg-white w-1/3 bottom-0 right-1/3 absolute md:mr-9 md:w-1/5 md:right-0 lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="rounded-full bg-$theme h-full"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Store -->
        <section id="stores" class="mx-8 md:mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="c-$theme pb-16 pt-4 text-center b-y b-white h-15 md:py-4">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2
              v-html="stores.heading"
              class="text-xl fw-bold px-4 py-2 bg-black inline relative lg:text-4xl"
              style="top: -2rem"
            />
          </div>
          <vf-carousel class="mt-10 w-full <md:px-6">
            <div
              v-for="({ address, name, to }, key) in stores.slides.slice(0, stores.count)"
              :key
              class="mb-15 px-10 py-6 b b-$theme bg-grey-10 w-full between"
              :class="stores.count > 1 ? 'mr-8 md:w-2/5 md:f-col' : 'f-col md:flex-row'"
            >
              <div class="<md:space-y-4" :class="{ 'space-y-8 mb-8': stores.count > 1 }">
                <h2 v-html="name" class="fw-bold lg:text-lg md:text-sm" />
                <p class="text-sm flex items-center lg:text-sm md:text-xs">
                  <vf-icon class="c-$theme" name="location" size="md" />
                  <span v-html="address" />
                </p>
              </div>
              <vf-button class="w-a <md:mt-4" :to variant="themable-inverse">
                {{ stores.cta }}
              </vf-button>
            </div>
            <template #prev="{ active, action }">
              <base-button
                v-if="stores.count > 1"
                :aria-label="$t.previous"
                class="bottom-0 right-3/4 absolute lg:right-1/5 md:right-1/4"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                v-if="stores.count > 1"
                :aria-label="$t.next"
                class="bottom-0 right-1/4 absolute md:right-0"
                :class="{ '<md:right-1/6': stores.count > 1, 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-if="stores.count > 1"
                v-slot="{ percentage }"
                class="rounded-full bg-white w-1/3 bottom-0 right-1/3 absolute md:mr-9 md:w-1/5 md:right-0 lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="rounded-full bg-$theme h-full"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Services -->
        <section id="services" class="mx-8 md:mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="c-$theme pb-16 pt-4 text-center b-y b-white h-15 md:py-4">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="text-xl fw-bold px-4 py-2 bg-black inline relative lg:text-4xl" style="top: -2rem">
              {{ services.heading }}
            </h2>
          </div>
          <vf-carousel class="mt-10 w-full <md:hidden">
            <div
              v-for="({ body, heading, icon }, key) in services.slides.flat()"
              :key
              class="mb-15 pr-8 w-1/4 space-y-4"
            >
              <base-picture v-bind="icon" class="mt-4" />
              <h2 class="text-sm fw-bold lg:text-lg">
                {{ heading }}
              </h2>
              <p class="text-xs lg:text-sm">
                {{ body }}
              </p>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="bottom-0 right-1/4 absolute lg:right-1/5"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="bottom-0 right-0 absolute"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="mr-9 rounded-full bg-white w-1/5 bottom-0 right-0 absolute lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="rounded-full bg-$theme h-full"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
          <vf-carousel class="mt-10 w-full md:hidden">
            <div v-for="(items, key) in services.slides" :key class="mb-10 w-full">
              <div
                v-for="({ body, heading, icon }, i) in items"
                :key="i"
                class="mx-10 space-y-4"
              >
                <base-picture v-bind="icon" class="mt-4" />
                <h2 class="text-lg fw-bold">
                  {{ heading }}
                </h2>
                <p class="text-sm">
                  {{ body }}
                </p>
              </div>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="bottom-0 right-3/4 absolute -mr-8"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="bottom-0 right-1/4 absolute"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="rounded-full bg-white w-1/3 bottom-0 right-1/3 absolute"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="rounded-full bg-$theme h-full"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Pricing -->
        <section id="pricing" class="md:mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="c-$theme py-4 text-center b-y b-white h-15 <md:mx-8">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="text-xl fw-bold px-4 py-2 bg-black inline relative lg:text-4xl" style="top: -2rem">
              {{ pricing.heading }}
            </h2>
          </div>
          <vf-carousel class="mt-10 w-full <md:pl-8">
            <div
              v-for="({ body, count, list, price, title }, key) in pricing.slides.slice(0, pricing.count)"
              :key
              class="mb-15 w-9/10"
              :class="pricing.count < 4 ? 'md:w-1/3' : 'md:w-2/5'"
            >
              <div class="mr-4 p-8 b b-$theme bg-grey-10 f-col h-full space-y-3">
                <h2 class="text-xl fw-bold">
                  {{ title }}
                </h2>
                <div class="flex">
                  <p class="text-6xl c-$theme fw-bold lh-none">
                    {{ price }}
                  </p>
                  <span class="text-xs c-white flex self-end">
                    &nbsp;{{ pricing.perItem }}
                  </span>
                </div>
                <p class="text-xs">
                  {{ body }}
                </p>
                <ul class="text-sm lg:text-base <md:space-y-3">
                  <li v-for="(item, i) in list.slice(0, count)" :key="i" class="fw-light flex">
                    <vf-icon class="c-$theme mr-1" name="success" size="md" /> {{ item }}
                  </li>
                </ul>
              </div>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="bottom-0 right-3/4 absolute lg:right-1/5 md:right-1/4"
                :class="{ 'md:hidden': pricing.count < 4, 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="bottom-0 right-1/4 absolute md:right-0"
                :class="{ 'md:hidden': pricing.count < 4, 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="rounded-full bg-white w-1/3 bottom-0 right-1/3 absolute md:mr-9 md:w-1/5 md:right-0 lg:w-1/6"
                :class="{ 'md:hidden': pricing.count < 4 }"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="rounded-full bg-$theme h-full"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Tutorials -->
        <section id="tutorials" class="mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="c-$theme py-4 text-center b-y b-white h-15 <md:mx-8">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="text-xl fw-bold px-4 py-2 bg-black inline relative lg:text-4xl" style="top: -2rem">
              Tutorials
            </h2>
          </div>
          <vf-carousel class="mt-10 w-full <md:hidden">
            <div
              v-for="({ body, heading, image }, key) in tutorials.slides"
              :key
              class="mb-20 pr-4 w-1/3 space-y-4"
            >
              <vf-link class="relative" variant="quieter" @click="tutorialsModal = true; activeIndex = key">
                <base-picture v-bind="tutorials.playIcon" class="m-a w-1/5 absolute-0" />
                <base-picture v-bind="image" />
              </vf-link>
              <p class="text-xs">
                {{ body }}
              </p>
              <h2 class="text-lg c-$theme fw-bold">
                {{ heading }}
              </h2>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="bottom-0 right-1/4 absolute lg:right-1/5"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="bottom-0 right-0 absolute"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="mr-9 rounded-full bg-white w-1/5 bottom-0 right-0 absolute lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="rounded-full bg-$theme h-full"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
          <vf-link
            v-for="({ body, heading }, key) in tutorials.slides"
            :key
            class="mx-15 my-10 flex md:hidden"
            variant="quieter"
            @click="tutorialsModal = true; activeIndex = key"
          >
            <div class="px-5">
              <p>
                {{ body }}
              </p>
              <h2 class="c-$theme fw-bold">
                {{ heading }}
              </h2>
            </div>
            <base-picture v-bind="tutorials.playIcon" class="w-1/6" />
          </vf-link>
        </section>
        <!-- Terms and conditions -->
        <section class="text-sm c-grey-40 pb-20 text-center">
          <vf-link to="/repair-service/terms-and-conditions">
            {{ termsAndConditions }}
          </vf-link>
        </section>
      </div>
    </div>
    <transition name="nested">
      <vf-modal v-if="tutorialsModal" size="lg" style-content="background: none" @resolve="tutorialsModal = false">
        <vf-carousel ref="scroller" loop @init="scroller.scroll.scrollToIndex(activeIndex, false)">
          <div v-for="({ image, videoSrc }, key) in tutorials.slides" :key class="m-a w-full">
            <span class="ml-5 md:ml-10">{{ key + 1 }}/{{ tutorials.slides.length }}</span>
            <base-video class="m-a w-4/5" :poster="image.src" :src="videoSrc" />
          </div>
        </vf-carousel>
      </vf-modal>
    </transition>
    <!-- Footer nav -->
    <section class="py-4 bg-$theme flex w-full bottom-0 sticky z-1 overflow-x-auto md:center">
      <template v-for="({ id, name }, key) in footerNav" :key>
        <vf-link :to="`#${id}`" variant="quieter">
          <p
            class="c-black fw-bold px-8 b-black w-max uppercase md:text-xs"
            :class="{ 'b-r': key + 1 !== footerNav.length }"
          >
            {{ name }}
          </p>
        </vf-link>
      </template>
    </section>
  </main>
</template>

<script lang="ts" setup>
const { $locale, $t } = useNuxtApp()
const { supportedLocales = [] } = useAppConfig().pages.repairService

definePageMeta({ header: { transparent: true } })

if (!supportedLocales.includes($locale))
  throw createError({ message: `Page is not available for ${$locale} locale`, statusCode: 404, fatal: true })

const { data, error } = await useApi().internal.getStaticPage('repair-service')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { hero, howItWorks, stores, services, pricing, tutorials, termsAndConditions, footerNav } = data.value
const activeIndex = ref()
const scroller = ref()
const tutorialsModal = ref(false)

const header = useHeaderStore()
</script>
